import * as React from 'react';

import { ICoordinates } from '../../types/newbuildingsCompare';
import {
  IDistrictFuture,
  IDistrictTaRating,
  IDistrictTransportAccessibility,
  IInfrastructurePoi,
} from '../../types/infrastructure';
import { getInfrastructureRequest } from '../../services/getInfrastructure';
import { mapDistrictPolygon } from '../../utils/infrastructure/mapDistrictPolygon';
import { mapFutureData } from '../../utils/infrastructure/mapFutureData';
import { mapPoisFromItems } from '../../utils/infrastructure/mapPoisFromItems';
import { mapTaRating } from '../../utils/infrastructure/mapTaRating';
import { mapTransportAccessibility } from '../../utils/infrastructure/mapTransportAccessibility';
import { useApplicationContext } from '../../utils/applicationContext';

/**
 * Профиль запроса — как в микрофронте инфраструктуры на карточке ЖК (map-search-frontend,
 * /v1/get-infrastructure-microfrontend/). Радиус не передаём по той же причине: с узким радиусом
 * бэк не отдаёт «Будущее района» — строящиеся объекты обычно дальше километра.
 */
const DISTRICT_INFRASTRUCTURE_PROFILE = 'newbuildingCard';

interface IUseDistrictInfrastructureParams {
  coordinates?: ICoordinates | null;
  newbuildingId?: number;
}

interface IUseDistrictInfrastructureResult {
  pois: IInfrastructurePoi[];
  /** Полигон ЖК — пустой, если бэк его не прислал. */
  polygon: ICoordinates[];
  /** «Будущее района» — null, если строящихся объектов нет. */
  future: IDistrictFuture | null;
  /** Точки транспортной доступности — null, если данных нет. */
  transportAccessibility: IDistrictTransportAccessibility | null;
  /** Рейтинг Дептранса — null, если данных нет. */
  taRating: IDistrictTaRating | null;
  load(): void;
}

/**
 * Лениво загружает данные района (точки инфраструктуры, полигон ЖК, будущее района,
 * транспортную доступность) одним запросом при первом открытии карты.
 * При ошибке запроса данные остаются пустыми — карта открывается без них (деградация).
 */
export function useDistrictInfrastructure({
  coordinates,
  newbuildingId,
}: IUseDistrictInfrastructureParams): IUseDistrictInfrastructureResult {
  const { httpApi, logger } = useApplicationContext();
  const [pois, setPois] = React.useState<IInfrastructurePoi[]>([]);
  const [polygon, setPolygon] = React.useState<ICoordinates[]>([]);
  const [future, setFuture] = React.useState<IDistrictFuture | null>(null);
  const [transportAccessibility, setTransportAccessibility] = React.useState<IDistrictTransportAccessibility | null>(
    null,
  );
  const [taRating, setTaRating] = React.useState<IDistrictTaRating | null>(null);
  const isRequestedRef = React.useRef(false);

  const load = React.useCallback(() => {
    if (isRequestedRef.current || !coordinates) {
      return;
    }

    isRequestedRef.current = true;

    getInfrastructureRequest({
      httpApi,
      parameters: newbuildingId
        ? { newbuildingId, profile: DISTRICT_INFRASTRUCTURE_PROFILE }
        : { coordinates, profile: DISTRICT_INFRASTRUCTURE_PROFILE },
    })
      .then(response => {
        setPois(mapPoisFromItems(response.infrastructure.items));
        setPolygon(mapDistrictPolygon(response.polygon));
        setFuture(mapFutureData(response.future, new Date().getFullYear()));
        setTransportAccessibility(mapTransportAccessibility(response.transportAccessibility));
        setTaRating(mapTaRating(response.transportAccessibilityRating));
      })
      .catch((error: Error) => {
        logger.error(error, { degradation: 'Инфраструктура района в сравнении ЖК' });
      });
  }, [coordinates, httpApi, logger, newbuildingId]);

  return { pois, polygon, future, transportAccessibility, taRating, load };
}
